import React from 'react';
import { Quotation, QuotationStatus } from '../../types/quotation';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import Modal from '../common/Modal';
import {
  DocumentIcon,
  PencilSquareIcon,
  ShareIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  EyeIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { customerDisplayName } from '../../utils/customerName';
import { QUOTATION_STATUS_META, StatusSelect, quotationTotal, fmtMoney, fmtDate, isExpired, toDate } from './quotationStatus';

interface QuotationViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: Quotation | null;
  onEdit: (quotation: Quotation) => void;
  onShare?: (quotation: Quotation) => void;
  onExportPdf?: (quotation: Quotation) => void;
  onStatusChange?: (quotation: Quotation, status: QuotationStatus) => void | Promise<void>;
  onCreateOrder?: (quotation: Quotation) => void;
}

const addr = (a?: { street?: string; city?: string; postalCode?: string; country?: string } | null) => {
  if (!a) return '';
  return [a.postalCode, a.city, a.street].filter(Boolean).join(' ');
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-sm text-gray-900 dark:text-gray-100 break-words">{children || '–'}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className = '' }) => (
  <section className={`crm-card p-4 ${className}`}>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">{title}</h3>
    {children}
  </section>
);

const QuotationViewModal: React.FC<QuotationViewModalProps> = ({
  isOpen, onClose, quotation, onEdit, onShare, onExportPdf, onStatusChange, onCreateOrder
}) => {
  const { organizations } = useOrganizations();
  const { customers } = useCustomers();
  const { t } = useLanguage();

  if (!quotation) return null;

  const organization = organizations?.find(o => o.id === quotation.organizationId);
  const customer = customers?.find(c => c.id === quotation.customerId);
  const currency = quotation.currency || 'HUF';
  const items = Array.isArray(quotation.items) ? quotation.items : [];
  const subtotal = typeof quotation.subtotal === 'number' ? quotation.subtotal : items.reduce((s, i) => s + (i.lineTotal || 0), 0);
  const taxAmount = typeof quotation.taxAmount === 'number' ? quotation.taxAmount : items.reduce((s, i) => s + ((i.lineTotalWithTax || 0) - (i.lineTotal || 0)), 0);
  const total = quotationTotal(quotation);
  const expired = isExpired(quotation);
  const meta = QUOTATION_STATUS_META[quotation.status] || QUOTATION_STATUS_META.draft;
  const shareViews = quotation.shareSettings?.viewCount || 0;
  const number = quotation.quotationNumber || quotation.number;
  const hasPod = !!(quotation.pod?.id || quotation.pod?.address || quotation.consumption || quotation.price);

  const customerEmail = (customer as any)?.email as string | undefined;
  const customerPhone = (customer as any)?.phone as string | undefined;
  const customerAddress = customer ? addr((customer as any).address) : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={number || t('quotations.viewDetails')} size="5xl" noPadding>
      <div className="flex flex-col min-h-full">
        {/* Dokumentum-fejléc */}
        <div className="px-4 sm:px-6 pt-4 pb-4 border-b border-[var(--crm-border)]">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Árajánlat</div>
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50 break-words">{number}</h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                {onStatusChange ? (
                  <StatusSelect value={quotation.status} onChange={(s) => onStatusChange(quotation, s)} />
                ) : (
                  <span className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium ${meta.chip}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}
                  </span>
                )}
                <span>Kelt: <span className="text-gray-800 dark:text-gray-200 tabular-nums">{fmtDate(quotation.date || quotation.createdAt)}</span></span>
                <span className="inline-flex items-center gap-1">
                  Érvényes: <span className="text-gray-800 dark:text-gray-200 tabular-nums">{fmtDate(quotation.validUntil)}</span>
                  {expired && <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><ExclamationTriangleIcon className="w-3.5 h-3.5" />lejárt</span>}
                </span>
                {quotation.paymentDue && <span>Fizetési határidő: <span className="text-gray-800 dark:text-gray-200">{quotation.paymentDue} nap</span></span>}
                {quotation.shareSettings && (
                  <span className="inline-flex items-center gap-1"><EyeIcon className="w-3.5 h-3.5" />{shareViews}× megnyitva</span>
                )}
              </div>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Bruttó végösszeg</div>
              <div className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">{fmtMoney(total, currency)}</div>
            </div>
          </div>
        </div>

        <div className="flex-1 p-4 sm:p-6 space-y-4">
          {quotation.customerResponse && (
            <div className={`flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-sm ${quotation.customerResponse.decision === 'accepted' ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300'}`}>
              <span className="font-medium">{quotation.customerResponse.decision === 'accepted' ? 'Az ügyfél elfogadta' : 'Az ügyfél elutasította'}</span>
              <span className="text-xs opacity-80">{[quotation.customerResponse.name, fmtDate(quotation.customerResponse.at)].filter(Boolean).join(' · ')}</span>
              {quotation.customerResponse.comment && <span className="text-xs opacity-80 basis-full">„{quotation.customerResponse.comment}”</span>}
            </div>
          )}
          {/* Felek */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Kiállító">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <BuildingOffice2Icon className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                </div>
                <div className="min-w-0 text-sm">
                  <div className="font-medium text-gray-900 dark:text-gray-50">{organization?.name || quotation.companyName || '–'}</div>
                  <div className="text-gray-600 dark:text-gray-300">{addr(organization?.address) || quotation.companyAddress}</div>
                  {(organization?.taxNumber || quotation.taxNumber) && <div className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Adószám: {organization?.taxNumber || quotation.taxNumber}</div>}
                  {quotation.representativeName && <div className="text-gray-500 dark:text-gray-400 text-xs">Képviselő: {quotation.representativeName}</div>}
                </div>
              </div>
            </Section>
            <Section title="Ügyfél">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                </div>
                <div className="min-w-0 text-sm">
                  <div className="font-medium text-gray-900 dark:text-gray-50">{customer ? customerDisplayName(customer) : '–'}</div>
                  {customerAddress && <div className="text-gray-600 dark:text-gray-300">{customerAddress}</div>}
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {customerEmail && <a href={`mailto:${customerEmail}`} className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"><EnvelopeIcon className="w-3.5 h-3.5" />{customerEmail}</a>}
                    {customerPhone && <a href={`tel:${customerPhone}`} className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"><PhoneIcon className="w-3.5 h-3.5" />{customerPhone}</a>}
                  </div>
                  {quotation.contactName && <div className="text-gray-500 dark:text-gray-400 text-xs">Kapcsolattartó: {quotation.contactName}</div>}
                </div>
              </div>
            </Section>
          </div>

          {/* Tételek */}
          <Section title={`Tételek (${items.length})`}>
            {items.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('quotations.form.sections.items.empty')}</p>
            ) : (
              <>
                {/* asztali */}
                <div className="hidden md:block overflow-x-auto -mx-4">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="text-left pl-4 pr-3 py-2 w-8">#</th>
                        <th className="text-left px-3 py-2">Megnevezés</th>
                        <th className="text-right px-3 py-2">Menny.</th>
                        <th className="text-right px-3 py-2">Egységár</th>
                        <th className="text-right px-3 py-2">Kedv.</th>
                        <th className="text-right px-3 py-2">ÁFA</th>
                        <th className="text-right px-3 py-2">Nettó</th>
                        <th className="text-right pl-3 pr-4 py-2">Bruttó</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => (
                        <tr key={i}>
                          <td className="pl-4 pr-3 py-2 text-xs text-gray-400 tabular-nums">{i + 1}</td>
                          <td className="px-3 py-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">{item.productName}</div>
                            {item.productCode && <div className="text-[11px] text-gray-500 dark:text-gray-400">{item.productCode}</div>}
                          </td>
                          <td className="px-3 py-2 text-right text-sm tabular-nums">{item.quantity}</td>
                          <td className="px-3 py-2 text-right text-sm tabular-nums">{fmtMoney(item.unitPrice ?? item.price, currency)}</td>
                          <td className="px-3 py-2 text-right text-sm tabular-nums text-gray-500">{item.discount ? `${item.discount}%` : '–'}</td>
                          <td className="px-3 py-2 text-right text-sm tabular-nums text-gray-500">{item.taxRate}%</td>
                          <td className="px-3 py-2 text-right text-sm tabular-nums">{fmtMoney(item.lineTotal || 0, currency)}</td>
                          <td className="pl-3 pr-4 py-2 text-right text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50">{fmtMoney(item.lineTotalWithTax || item.totalWithTax || 0, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* mobil */}
                <ul className="md:hidden divide-y divide-[var(--crm-border)]">
                  {items.map((item, i) => (
                    <li key={i} className="py-2.5 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-50 break-words">{item.productName}</div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                          {item.quantity} × {fmtMoney(item.unitPrice ?? item.price, currency)}{item.discount ? ` · −${item.discount}%` : ''} · ÁFA {item.taxRate}%
                        </div>
                      </div>
                      <div className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-50 whitespace-nowrap">{fmtMoney(item.lineTotalWithTax || item.totalWithTax || 0, currency)}</div>
                    </li>
                  ))}
                </ul>
                {/* összesítés */}
                <div className="mt-3 pt-3 border-t border-[var(--crm-border)] flex justify-end">
                  <dl className="w-full sm:w-72 text-sm space-y-1">
                    <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">Nettó</dt><dd className="tabular-nums">{fmtMoney(subtotal, currency)}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500 dark:text-gray-400">ÁFA</dt><dd className="tabular-nums">{fmtMoney(taxAmount, currency)}</dd></div>
                    <div className="flex justify-between pt-1 border-t border-[var(--crm-border)] text-base font-semibold"><dt>Bruttó</dt><dd className="tabular-nums">{fmtMoney(total, currency)}</dd></div>
                  </dl>
                </div>
              </>
            )}
          </Section>

          {/* Megjegyzés + dokumentumok */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quotation.notes && (
              <Section title="Megjegyzés">
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{quotation.notes}</p>
              </Section>
            )}
            {quotation.documents && quotation.documents.length > 0 && (
              <Section title={`Dokumentumok (${quotation.documents.length})`}>
                <ul className="space-y-1.5">
                  {quotation.documents.map((d, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-sm">
                      <span className="inline-flex items-center gap-2 min-w-0"><DocumentIcon className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="truncate">{d.name}</span></span>
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary-600 hover:underline flex-shrink-0">{t('common.download')}</a>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          {/* Örökölt energia-adatok – csak ha van kitöltve */}
          {hasPod && (
            <Section title="Fogyasztási és mérési adatok">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Ár">{quotation.price ? `${quotation.price} ${quotation.priceUnit || ''}` : ''}</Field>
                <Field label="Fogyasztás">{quotation.consumption ? `${quotation.consumption} kWh` : ''}</Field>
                <Field label="Szerződés kezdete">{quotation.contractStart ? fmtDate(quotation.contractStart) : ''}</Field>
                <Field label="Szerződés vége">{quotation.contractEnd ? fmtDate(quotation.contractEnd) : ''}</Field>
                <Field label="POD azonosító">{quotation.pod?.id}</Field>
                <Field label="POD cím">{quotation.pod?.address}</Field>
                <Field label="Mérés típusa">{quotation.pod?.measurementType}</Field>
                <Field label="Profilszám">{quotation.pod?.profileNumber}</Field>
              </div>
              {quotation.kat?.text && <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">{quotation.kat.text}</p>}
            </Section>
          )}

          <div className="text-[11px] text-gray-400 dark:text-gray-500">
            Létrehozva: {fmtDate(quotation.createdAt)}{toDate(quotation.updatedAt) ? ` · Módosítva: ${fmtDate(quotation.updatedAt)}` : ''}
          </div>
        </div>

        {/* Műveletsáv */}
        <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="crm-btn crm-btn-ghost mr-auto">Bezárás</button>
            {onExportPdf && (
              <button type="button" onClick={() => onExportPdf(quotation)} className="crm-btn crm-btn-secondary"><DocumentArrowDownIcon className="w-4 h-4 mr-1.5" />PDF</button>
            )}
            {onShare && (
              <button type="button" onClick={() => onShare(quotation)} className="crm-btn crm-btn-secondary"><ShareIcon className="w-4 h-4 mr-1.5" />Küldés / megosztás</button>
            )}
            {onCreateOrder && (
              <button type="button" onClick={() => onCreateOrder(quotation)} className={`crm-btn ${(quotation.status === 'accepted' || quotation.status === 'success') ? 'crm-btn-primary' : 'crm-btn-secondary'}`}><ShoppingCartIcon className="w-4 h-4 mr-1.5" />Megrendelés</button>
            )}
            <button type="button" onClick={() => onEdit(quotation)} className={`crm-btn ${(quotation.status === 'accepted' || quotation.status === 'success') && onCreateOrder ? 'crm-btn-secondary' : 'crm-btn-primary'}`}><PencilSquareIcon className="w-4 h-4 mr-1.5" />Szerkesztés</button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default QuotationViewModal;
